import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import styles from './index.module.scss'
import Header from '../../components/Header'
import { useUser } from 'cor-business'
import { useWorkspace } from '../../context/workspace.context'
import PlusIcon from '../../assets/icon/PlusIcon'
import ProjectService from '../../services/project.service'
import Statistics from '../Statistics'
import MyTasksWidget from './components/MyTasksWidget'

const HOME_WIDGETS_STORAGE_KEY = 'home_widget_ids'

interface WidgetCatalogItem {
    id: string
    name: string
    description: string
}

function getGreeting(): string {
    const hour = new Date().getHours()
    if (hour >= 5 && hour < 12) return 'Доброе утро'
    if (hour >= 12 && hour < 17) return 'Добрый день'
    if (hour >= 17 && hour < 22) return 'Добрый вечер'
    return 'Доброй ночи'
}

function getUserFirstName(user: any): string {
    return user?.profile?.first_name || user?.first_name || user?.email || 'Пользователь'
}

function normalizeWidgets(data: any): WidgetCatalogItem[] {
    const rawWidgets: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.widgets)
            ? data.widgets
            : Array.isArray(data?.items)
                ? data.items
                : []

    return rawWidgets
        .filter((item) => item && (item.id != null || item.name))
        .map((item, index) => ({
            id: String(item.id ?? `${item.name ?? 'widget'}-${index}`),
            name: item.name || 'Виджет без названия',
            description: item.description || '',
        }))
}

function isStatisticsWidget(widget: WidgetCatalogItem): boolean {
    const name = widget.name.toLowerCase()
    return name.includes('стат') || name.includes('stat')
}

function isMyTasksWidget(widget: WidgetCatalogItem): boolean {
    const name = widget.name.toLowerCase()
    return name.includes('my tasks') || name.includes('мои задач')
}

function WidgetPreviewIcon({ name }: { name: string }) {
    const value = name.toLowerCase()

    if (value.includes('стат') || value.includes('stat')) {
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.25608 2.46461C9.99418 2.30818 10.6344 2.91742 10.6344 3.67898V11.8009C10.6344 12.0314 10.716 12.2537 10.8587 12.4307L16.2455 18.9924C16.7226 19.5728 16.6207 20.4496 15.9642 20.8078C14.5736 21.5735 12.9792 22.0098 11.2869 22.0098C5.8837 22.0098 1.5 17.5846 1.5 12.1302C1.5 7.37561 4.82345 3.40729 9.25608 2.46461Z" fill="white" />
                <path d="M13.4221 11.3031H20.5991C21.3209 11.3031 21.9122 10.7103 21.8184 9.98992C21.2394 5.58526 17.7813 2.09445 13.418 1.50991C12.7044 1.41523 12.1172 2.01212 12.1172 2.74075V9.98581C12.1172 10.7144 12.7003 11.3031 13.4221 11.3031Z" fill="#DAD8FE" />
                <path d="M21.798 13.279H19.1882H19.1841H15.9952C14.8942 13.279 14.2866 14.5757 14.9921 15.4361L18.4338 19.6226C18.8742 20.1618 19.6612 20.2647 20.1628 19.7831C21.5737 18.4329 22.585 16.6628 23.001 14.6704C23.1559 13.9253 22.5524 13.279 21.798 13.279Z" fill="#DAD8FE" />
            </svg>
        )
    }

    if (value.includes('задач') || value.includes('task')) {
        return (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M6.21673 3.88973C6.21673 3.39835 6.61508 3 7.10646 3H17.3726C19.376 3 21 4.62403 21 6.62738V16.8935C21 17.3849 20.6017 17.7833 20.1103 17.7833C19.6189 17.7833 19.2205 17.3849 19.2205 16.8935V6.62738C19.2205 5.6068 18.3932 4.77947 17.3726 4.77947H7.10646C6.61508 4.77947 6.21673 4.38112 6.21673 3.88973Z" fill="#DAD8FE" />
                <path fillRule="evenodd" clipRule="evenodd" d="M4.36882 5.94297C3.61284 5.94297 3 6.55581 3 7.31179V19.6312C3 20.3872 3.61284 21 4.36882 21H16.6882C17.4442 21 18.057 20.3872 18.057 19.6312V7.31179C18.057 6.55581 17.4442 5.94297 16.6882 5.94297H4.36882ZM8.96578 16.0288L7.03118 14.0942C6.67529 13.7383 6.67529 13.1634 7.03118 12.8075C7.38707 12.4516 7.96198 12.4516 8.31787 12.8075L9.60456 14.0942L12.835 10.8637C13.1909 10.5079 13.7658 10.5079 14.1217 10.8637C14.4776 11.2196 14.4776 11.7945 14.1217 12.1504L10.2525 16.0196C9.9057 16.3847 9.32167 16.3847 8.96578 16.0288Z" fill="white" />
            </svg>
        )
    }

    if (value.includes('проект') || value.includes('project')) {
        return (
            <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2.75" y="4.75" width="7.5" height="5.5" rx="1.5" stroke="white" strokeWidth="1.8" />
                <rect x="11.75" y="11.75" width="7.5" height="5.5" rx="1.5" stroke="white" strokeWidth="1.8" />
                <path d="M10.25 7.5H12.25C13.3546 7.5 14.25 8.39543 14.25 9.5V11.75" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        )
    }

    return (
        <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="6" height="6" rx="2" fill="white" />
            <rect x="13" y="3" width="6" height="6" rx="2" fill="white" fillOpacity="0.82" />
            <rect x="3" y="13" width="6" height="6" rx="2" fill="white" fillOpacity="0.82" />
            <rect x="13" y="13" width="6" height="6" rx="2" fill="white" />
        </svg>
    )
}

export default function Home() {
    const navigate = useNavigate()
    const { workspace } = useWorkspace()
    const { user } = useUser()
    const widgetsRestoredRef = useRef(false)
    const widgetDropTargetRef = useRef<{id: string; position: 'before' | 'after'} | null>(null)
    const [catalog, setCatalog] = useState<WidgetCatalogItem[]>([])
    const [addedWidgetIds, setAddedWidgetIds] = useState<string[]>([])
    const [isCatalogOpen, setIsCatalogOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')
    const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null)
    const [widgetDropTarget, setWidgetDropTarget] = useState<{id: string; position: 'before' | 'after'} | null>(null)
    const [widgetDragPointer, setWidgetDragPointer] = useState<{x: number; y: number} | null>(null)
    const greeting = getGreeting()
    const firstName = getUserFirstName(user)

    useEffect(() => {
        document.documentElement.classList.add('home-scroll-lock')
        document.body.classList.add('home-scroll-lock')

        return () => {
            document.documentElement.classList.remove('home-scroll-lock')
            document.body.classList.remove('home-scroll-lock')
        }
    }, [])

    useEffect(() => {
        let isMounted = true

        const fetchWidgets = async () => {
            try {
                setIsLoading(true)
                const data = await ProjectService.getWidgets()
                if (!isMounted) {
                    return
                }

                setCatalog(normalizeWidgets(data))
                setError('')
            } catch (e) {
                console.error('Failed to fetch widgets:', e)
                if (!isMounted) {
                    return
                }

                setError('Не удалось загрузить каталог виджетов.')
            } finally {
                if (isMounted) {
                    setIsLoading(false)
                }
            }
        }

        fetchWidgets()

        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (!catalog.length) {
            return
        }

        const existingIds = new Set(catalog.map((widget) => widget.id))
        const savedWidgetIds = (() => {
            try {
                const raw = localStorage.getItem(HOME_WIDGETS_STORAGE_KEY)
                const parsed = raw ? JSON.parse(raw) : []
                return Array.isArray(parsed) ? parsed.map((id) => String(id)) : []
            } catch (e) {
                console.error('Failed to restore home widgets from localStorage:', e)
                return []
            }
        })()

        setAddedWidgetIds(savedWidgetIds.filter((id) => existingIds.has(id)))
        widgetsRestoredRef.current = true
    }, [catalog])

    useEffect(() => {
        if (!widgetsRestoredRef.current) {
            return
        }

        try {
            localStorage.setItem(HOME_WIDGETS_STORAGE_KEY, JSON.stringify(addedWidgetIds))
        } catch (e) {
            console.error('Failed to save home widgets to localStorage:', e)
        }
    }, [addedWidgetIds])

    const addedWidgets = useMemo(
        () => addedWidgetIds
            .map((id) => catalog.find((widget) => widget.id === id))
            .filter((widget): widget is WidgetCatalogItem => Boolean(widget)),
        [addedWidgetIds, catalog],
    )

    const handleAddWidget = (widgetId: string) => {
        if (addedWidgetIds.includes(widgetId)) {
            return
        }

        setAddedWidgetIds((prev) => [...prev, widgetId])
    }

    const handleRemoveWidget = (widgetId: string) => {
        setAddedWidgetIds((prev) => prev.filter((id) => id !== widgetId))
    }

    const handleDropWidget = (targetWidgetId: string, position: 'before' | 'after', sourceWidgetId?: string) => {
        const draggedId = sourceWidgetId !== undefined && sourceWidgetId !== ''
            ? sourceWidgetId
            : draggedWidgetId
        if (draggedId === null || draggedId === targetWidgetId) return

        setAddedWidgetIds((currentIds) => {
            if (!currentIds.includes(draggedId) || !currentIds.includes(targetWidgetId)) return currentIds

            const nextIds = currentIds.filter((id) => id !== draggedId)
            const targetIndex = nextIds.indexOf(targetWidgetId)
            nextIds.splice(targetIndex + (position === 'after' ? 1 : 0), 0, draggedId)
            return nextIds
        })
        widgetDropTargetRef.current = null
        setWidgetDropTarget(null)
        setWidgetDragPointer(null)
        setDraggedWidgetId(null)
    }

    useEffect(() => {
        if (draggedWidgetId === null) return

        const updateDropTarget = (event: PointerEvent) => {
            event.preventDefault()
            setWidgetDragPointer({x: event.clientX, y: event.clientY})

            const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-home-widget-index]'))
            const candidates = cards.filter((card) => card.dataset.homeWidgetId !== draggedWidgetId)
            if (!candidates.length) return

            const elementAtPointer = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null
            let targetCard = elementAtPointer?.closest<HTMLElement>('[data-home-widget-index]') || null

            if (!targetCard || targetCard.dataset.homeWidgetId === draggedWidgetId) {
                targetCard = candidates.reduce((nearest, card) => {
                    const cardBounds = card.getBoundingClientRect()
                    const nearestBounds = nearest.getBoundingClientRect()
                    const cardDistance = Math.abs(event.clientY - (cardBounds.top + cardBounds.height / 2))
                    const nearestDistance = Math.abs(event.clientY - (nearestBounds.top + nearestBounds.height / 2))
                    return cardDistance < nearestDistance ? card : nearest
                })
            }

            const targetIndex = Number(targetCard.dataset.homeWidgetIndex)
            const targetWidget = addedWidgets[targetIndex]
            if (!targetWidget || targetWidget.id === draggedWidgetId) return

            const bounds = targetCard.getBoundingClientRect()
            const nextTarget = {
                id: targetWidget.id,
                position: event.clientY < bounds.top + bounds.height / 2 ? 'before' as const : 'after' as const,
            }

            widgetDropTargetRef.current = nextTarget
            setWidgetDropTarget((currentTarget) => (
                currentTarget?.id === nextTarget.id && currentTarget.position === nextTarget.position
                    ? currentTarget
                    : nextTarget
            ))
        }

        const finishPointerDrag = () => {
            const target = widgetDropTargetRef.current
            if (target) {
                handleDropWidget(target.id, target.position, draggedWidgetId)
                return
            }

            widgetDropTargetRef.current = null
            setWidgetDropTarget(null)
            setWidgetDragPointer(null)
            setDraggedWidgetId(null)
        }

        const cancelPointerDrag = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            widgetDropTargetRef.current = null
            setWidgetDropTarget(null)
            setWidgetDragPointer(null)
            setDraggedWidgetId(null)
        }

        window.addEventListener('pointermove', updateDropTarget, {passive: false})
        window.addEventListener('pointerup', finishPointerDrag)
        window.addEventListener('pointercancel', finishPointerDrag)
        window.addEventListener('keydown', cancelPointerDrag)
        return () => {
            window.removeEventListener('pointermove', updateDropTarget)
            window.removeEventListener('pointerup', finishPointerDrag)
            window.removeEventListener('pointercancel', finishPointerDrag)
            window.removeEventListener('keydown', cancelPointerDrag)
        }
    }, [addedWidgets, draggedWidgetId])

    const getWidgetDragHandleProps = (widgetId: string) => ({
        onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
            if (event.button !== 0) return
            event.preventDefault()
            widgetDropTargetRef.current = null
            setWidgetDropTarget(null)
            setWidgetDragPointer({x: event.clientX, y: event.clientY})
            setDraggedWidgetId(widgetId)
        },
    })

    return (
        <>
            <Header />
            <div className={styles.home}>
                <div className={styles.home__topBar}>
                    <nav className={styles.home__breadcrumbs} aria-label="Хлебные крошки">
                        <button
                            type="button"
                            className={styles.home__breadcrumbLink}
                            title={workspace?.name || 'Рабочее пространство'}
                            onClick={() => navigate('/workspaces')}
                        >
                            {workspace?.name || 'Рабочее пространство'}
                        </button>
                        <span className={styles.home__breadcrumbSeparator}>/</span>
                        <span className={styles.home__breadcrumbCurrent}>Домой</span>
                    </nav>
                    <button
                        type="button"
                        className={styles.home__widgetsBtn}
                        onClick={() => setIsCatalogOpen(true)}
                    >
                        <PlusIcon />
                        <span>Добавить виджеты</span>
                    </button>
                </div>

                <div className={styles.home__body}>
                    <div className={styles.home__content}>
                        <div
                            className={
                                `${styles.home__intro} ${addedWidgets.length === 0 ? styles.home__introCentered : ''} ${addedWidgets.length === 0 && isCatalogOpen ? styles.home__introCenteredWithCatalog : ''}`
                            }
                        >
                            <h1 className={styles.home__greeting}>
                                {greeting}, {firstName}
                            </h1>
                            <p className={styles.home__description}>
                                Вы можете настроить экран "Домой" как вам удобно, добавив сюда
                                информативные виджеты с помощью кнопки "Добавить виджеты"
                            </p>
                        </div>

                        {addedWidgets.length > 0 && (
                            <div className={`${styles.home__widgetsList} ${draggedWidgetId !== null ? styles.home__widgetsListDragging : ''}`}>
                                {addedWidgets.map((widget, widgetIndex) => (
                                    <article
                                        key={widget.id}
                                        data-home-widget-id={widget.id}
                                        data-home-widget-index={widgetIndex}
                                        className={`${styles.home__widgetCard} ${(isStatisticsWidget(widget) || isMyTasksWidget(widget)) ? styles.home__widgetCardStatistics : ''} ${draggedWidgetId === widget.id ? styles.home__widgetCardDragging : ''} ${widgetDropTarget?.id === widget.id && widgetDropTarget.position === 'before' ? styles.home__widgetDropBefore : ''} ${widgetDropTarget?.id === widget.id && widgetDropTarget.position === 'after' ? styles.home__widgetDropAfter : ''}`}
                                    >
                                        {isStatisticsWidget(widget) ? (
                                            <Statistics
                                                onRemove={() => handleRemoveWidget(widget.id)}
                                                dragHandleProps={getWidgetDragHandleProps(widget.id)}
                                            />
                                        ) : isMyTasksWidget(widget) ? (
                                            <MyTasksWidget
                                                onRemove={() => handleRemoveWidget(widget.id)}
                                                dragHandleProps={getWidgetDragHandleProps(widget.id)}
                                            />
                                        ) : (
                                            <>
                                                <div className={styles.home__widgetCardHeader}>
                                                <div className={styles.home__widgetCardIcon}>
                                                    <WidgetPreviewIcon name={widget.name} />
                                                </div>
                                                    <button
                                                        type="button"
                                                        className={styles.home__widgetAction}
                                                        onClick={() => handleRemoveWidget(widget.id)}
                                                        aria-label={`Убрать виджет ${widget.name}`}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M3 7H11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                <h3 className={styles.home__widgetCardTitle}>{widget.name}</h3>
                                                {widget.description && (
                                                    <p className={styles.home__widgetCardDescription}>{widget.description}</p>
                                                )}
                                            </>
                                        )}
                                    </article>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {draggedWidgetId !== null && widgetDragPointer && (
                    <div
                        className={styles.home__widgetDragPreview}
                        style={{left: widgetDragPointer.x + 18, top: widgetDragPointer.y + 18}}
                    >
                        <span className={styles.home__widgetDragPreviewHandle}>⠿</span>
                        <span>{catalog.find((widget) => widget.id === draggedWidgetId)?.name || 'Виджет'}</span>
                    </div>
                )}

                {isCatalogOpen && (
                        <aside className={styles.home__catalog}>
                            <div className={styles.home__catalogHeader}>
                                <div className={styles.home__catalogTitle}>Добавить карточки</div>
                                <button
                                    type="button"
                                    className={styles.home__catalogClose}
                                    onClick={() => setIsCatalogOpen(false)}
                                    aria-label="Закрыть каталог виджетов"
                                >
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                        <path d="M13.5 4.5L4.5 13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>

                            <div className={styles.home__catalogBody}>
                                {isLoading ? (
                                    <div className={styles.home__catalogState}>Загрузка виджетов...</div>
                                ) : error ? (
                                    <div className={styles.home__catalogState}>{error}</div>
                                ) : catalog.length === 0 ? (
                                    <div className={styles.home__catalogState}>Каталог виджетов пока пуст.</div>
                                ) : (
                                    <div className={styles.home__catalogList}>
                                        {catalog.map((widget) => {
                                            const isAdded = addedWidgetIds.includes(widget.id)

                                            return (
                                                <article key={widget.id} className={styles.home__catalogCard}>
                                                    <div className={styles.home__catalogCardHeader}>
                                                        <div className={styles.home__catalogCardIcon}>
                                                            <WidgetPreviewIcon name={widget.name} />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className={`${styles.home__catalogCardAction} ${isAdded ? styles.home__catalogCardActionAdded : ''}`}
                                                            onClick={() => isAdded ? handleRemoveWidget(widget.id) : handleAddWidget(widget.id)}
                                                            aria-label={isAdded ? `Скрыть виджет ${widget.name}` : `Добавить виджет ${widget.name}`}
                                                        >
                                                            {isAdded ? (
                                                                <>
                                                                    <span className={styles.home__catalogCardActionCheck}>
                                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M5.5 12.5L10 17L18.5 8.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    </span>
                                                                    <span className={styles.home__catalogCardActionRemove}>
                                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            <path d="M6 12H18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                                                        </svg>
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <rect width="32" height="32" rx="16" fill="#F6F5F9" />
                                                                    <path fillRule="evenodd" clipRule="evenodd" d="M16 9C16.5523 9 17 9.44772 17 10V22C17 22.5523 16.5523 23 16 23C15.4477 23 15 22.5523 15 22V10C15 9.44772 15.4477 9 16 9Z" fill="#B1AEC7" />
                                                                    <path fillRule="evenodd" clipRule="evenodd" d="M9 16C9 15.4477 9.44772 15 10 15L22 15C22.5523 15 23 15.4477 23 16C23 16.5523 22.5523 17 22 17H10C9.44772 17 9 16.5523 9 16Z" fill="#B1AEC7" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                    </div>
                                                    <h3 className={styles.home__catalogCardTitle}>{widget.name}</h3>
                                                    {widget.description && (
                                                        <p className={styles.home__catalogCardDescription}>{widget.description}</p>
                                                    )}
                                                </article>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        </aside>
                    )}
            </div>
        </>
    )
}
